import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { CommentCard } from '@/components/CommentCard'
import { postsApi, User } from '@/lib/api'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { getInitials } from '@/lib/utils'

interface CommentSectionProps {
  postId: string
  currentUser?: User | null
  isExpanded: boolean
}

export function CommentSection({ postId, currentUser, isExpanded }: CommentSectionProps) {
  const [commentContent, setCommentContent] = useState('')
  const queryClient = useQueryClient()

  const { data: comments, isLoading } = useQuery({
    queryKey: ['comments', postId],
    queryFn: () => postsApi.getComments(postId, 20),
    enabled: isExpanded,
  })

  const createCommentMutation = useMutation({
    mutationFn: (content: string) => postsApi.createComment(postId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] })
      queryClient.invalidateQueries({ queryKey: ['feed'] })
      setCommentContent('')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (commentContent.trim()) {
      createCommentMutation.mutate(commentContent)
    }
  }

  if (!isExpanded) {
    return null
  }

  const displayName = currentUser?.displayName || currentUser?.display_name || 'User'
  const avatarUrl = currentUser?.avatarUrl

  return (
    <div className="border-t pt-4 space-y-4">
      {/* Comment Input */}
      {currentUser && (
        <form onSubmit={handleSubmit} className="flex gap-3">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage src={avatarUrl} />
            <AvatarFallback className="text-xs">{getInitials(displayName)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-2">
            <Textarea
              value={commentContent}
              onChange={(e) => setCommentContent(e.target.value)}
              placeholder="Write a comment..."
              className="min-h-[60px] resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  handleSubmit(e)
                }
              }}
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                size="sm"
                disabled={!commentContent.trim() || createCommentMutation.isPending}
              >
                {createCommentMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-3 w-3" />
                    Comment
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !comments?.data || comments.data.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">
              No comments yet. Be the first to comment!
            </p>
          </div>
        ) : (
          comments.data.map((comment) => (
            <CommentCard
              key={comment.comment.id}
              comment={comment}
              postId={postId}
              currentUser={currentUser}
            />
          ))
        )}
      </div>
    </div>
  )
}
